import React, { useContext } from 'react'
import {
  sessionContext,
  useGetAllBusinesses,
  useGetBusinessIfExists,
} from '@marketing-milk/frontend'
import { Business, BusinessError, Permissions } from '@marketing-milk/interfaces'
import { ErrorOutline } from '@mui/icons-material'

export type AccessErrorCause = 'authorizer' | 'adAccount' | 'unknown'

// ad_account_access_error and page_access_error both block ad builder from working correctly (ad
// account access blocks campaign management outright; page access blocks posting/publishing) — critical.
// offline_upload_error and lead_access_error degrade one specific feature but don't block ad uploads
// or campaign delivery — warning only.
const CRITICAL_ERRORS = [BusinessError.ad_account_access_error, BusinessError.page_access_error]

// DRI-442: fb_system_user_mismatch is a warning, not critical, because the connections themselves work for
// reporting. It's only set when Facebook confirms the ad account's system user has no Advertise on the page, which
// publishing needs, so unlike the other warnings this one never says "nothing is blocked". Shared with the
// publish screens. DRI-462: shown as the "Ads Publish Warning", named for what it blocks, with the fix spelled out.
export const ADS_PUBLISH_WARNING_TITLE = 'Ads Publish Warning.'
export const ADS_PUBLISH_WARNING_MESSAGE =
  'Publishing from the ad builder is blocked: this business\'s Ad Account and Page are on different Facebook system users, and the Ad Account\'s has no Advertise access on the Page, so Facebook rejects its ads with "Insufficient Page Permission to Run Ads". The fix is to use one system user for both.'

/**
 * `businesses.errors` carries no payload — it records that a Facebook call failed, not why. The
 * only signal available client-side is whether the businesses sharing this ad authorizer are also
 * failing, so every cause below is a hedge rather than a diagnosis.
 */
export function inferAccessCause(business: Business, allBusinesses: Business[]): AccessErrorCause {
  const authorizerID = business.fbAdAuthorizerID
  if (authorizerID === null || authorizerID === undefined) return 'unknown'

  const siblings = allBusinesses.filter(
    _ => _.id !== business.id && _.fbAdAuthorizerID === authorizerID
  )
  if (siblings.length === 0) return 'unknown'

  const allSiblingsFailing = siblings.every(_ =>
    (_.errors ?? []).some(e => CRITICAL_ERRORS.includes(e))
  )
  return allSiblingsFailing ? 'authorizer' : 'adAccount'
}

// Kept short on purpose — the banner costs vertical space on already-cramped pages, so it carries
// only what the admin panel can't work out for itself: whether the authorizer's other businesses
// are failing too. Everything else lives behind the link.
const accessCauseText: Record<AccessErrorCause, string> = {
  authorizer: 'likely the ad authorizer',
  adAccount: 'likely this ad account or page',
  unknown: 'cause unclear',
}

// The business's Facebook settings in the admin panel. The theme's anchor styling only shows on hover, which
// leaves the one action in a notice looking like plain text, so it's underlined explicitly.
export function AdminFixLink({ businessID }: { businessID: number }) {
  return (
    <a
      href={`${process.env.REACT_APP_ADMIN}/business/beta/accounts/edit/${businessID}`}
      target="_blank"
      rel="noreferrer"
      className="underline font-semibold hover:opacity-80"
    >
      Fix it in the admin panel ↗
    </a>
  )
}

export function BusinessErrorBanner() {
  const business = useGetBusinessIfExists()
  const businesses = useGetAllBusinesses()
  const { user } = useContext(sessionContext)

  const errors = business?.errors ?? []
  if (!business || errors.length === 0) return null

  const hasCriticalError = errors.some(e => CRITICAL_ERRORS.includes(e))
  const hasOfflineError = errors.includes(BusinessError.offline_upload_error)
  const hasLeadError = errors.includes(BusinessError.lead_access_error)
  const hasSystemUserMismatch = errors.includes(BusinessError.fb_system_user_mismatch)
  const accessCause = hasCriticalError ? inferAccessCause(business, businesses) : undefined
  const canAccessAdmin = !!user?.permissions.includes(Permissions.AccessAdminApp)

  // critical (ad/page access) genuinely blocks ad builder, so it gets its own red banner with no
  // "nothing is blocked" reassurance; offline/lead errors are real but non-blocking, so they're kept
  // in a separate amber banner that still carries the "nothing is blocked" framing.
  const criticalColors = 'bg-red-50 border-b-2 border-red-600 text-red-900'
  const warningColors = 'bg-amber-50 border-b-2 border-amber-500 text-amber-900'

  const fixLink = canAccessAdmin ? (
    <>
      <AdminFixLink businessID={business.id} />, then refresh this page.
    </>
  ) : (
    'Ask someone with admin access to resolve it.'
  )

  return (
    // Deliberately Tailwind rather than a MUI Alert. The app is wrapped in MUI v4's ThemeProvider
    // with no `StylesProvider injectFirst`, so v4's JSS sheets inject after v5's emotion sheets as
    // components mount. `.MuiPaper-root { background: #fff }` from v4 then beats a v5 Alert's
    // severity background on equal specificity, and the banner turns white after a navigation.
    <>
      {hasCriticalError && accessCause && (
        <div role="alert" className={`w-full flex items-center gap-3 px-4 py-2 ${criticalColors}`}>
          <ErrorOutline className="text-red-600 flex-shrink-0" />
          <span>
            <span className="font-semibold">
              Facebook is rejecting requests for this ad account or page
            </span>{' '}
            ({accessCauseText[accessCause]}). This is blocking ad builder. {fixLink}
          </span>
        </div>
      )}
      {hasSystemUserMismatch && (
        <div role="alert" className={`w-full flex items-center gap-3 px-4 py-2 ${warningColors}`}>
          <ErrorOutline className="text-amber-600 flex-shrink-0" />
          <span>
            <span className="font-semibold">{ADS_PUBLISH_WARNING_TITLE}</span>{' '}
            {ADS_PUBLISH_WARNING_MESSAGE} {fixLink}
          </span>
        </div>
      )}
      {(hasOfflineError || hasLeadError) && (
        <div role="alert" className={`w-full flex items-center gap-3 px-4 py-2 ${warningColors}`}>
          <ErrorOutline className="text-amber-600 flex-shrink-0" />
          <span>
            {hasOfflineError && (
              <>
                <span className="font-semibold">Offline conversion uploads are failing</span> —
                affects conversion-optimized campaigns only.{' '}
              </>
            )}
            {hasLeadError && (
              <>
                <span className="font-semibold">Lead access is failing</span> — affects lead syncing
                only.{' '}
              </>
            )}
            Nothing is blocked. {fixLink}
          </span>
        </div>
      )}
    </>
  )
}
